import type { FC } from 'react'

import { config } from '@skytech/config'
import { getEnvironmentBadgeLabel } from '@skytech/config/environmentBadge'
import { Select } from '@skytech/ui'
import { Image } from '@skytech/ui/Image'
import { availableLanguages } from '@skytech/ui/LanguageChoiceButtons'
import { WHITE_LABEL_LIST } from '@skytech/utils'
import AccountCircleIcon from 'mdi-react/AccountCircleIcon'
import CloseIcon from 'mdi-react/CloseIcon'
import TogglerIcon from 'mdi-react/MenuIcon'
import { Link } from 'react-router-dom'
import { twJoin, twMerge } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import BechtleLogo from '@/assets/img/bechtle/logo-bechtle.png'
import CsiLogo from '@/assets/img/csi/logo.png'
import ElkjopLogo from '@/assets/img/elkjop/logo-elkjop-plain.svg'
import IkommLogo from '@/assets/img/ikomm/ikomm_control_logo_white.png'
import KomplettLogo from '@/assets/img/komplett/komplett-logo.png'
import MobitLogo from '@/assets/img/mobit/mobit-logo.png'
import SkytechLogo from '@/assets/img/skytech/logo-white.png'
import TeliaLogo from '@/assets/img/telia/telia_logo_text.svg'
import ThreeStepITLogo from '@/assets/img/threeStepIT/threeStepIT-logo.svg'
import TietoEvryLogo from '@/assets/img/tieto-evry/tietoevry_logo_white.svg'
import { useTranslation } from '@/components/i18n/hooks'
import IceLogo from '@/gfx/icelogo_white.png'
import useUser, { useIsSkytech, useIsThreeStepIT } from '@/hooks/user'
import { phoneNumber } from '@/util/formatters/text'

import { NotificationBell } from './NotificationBell'

import './style/banner.scss'

export const Banner = () => {
  const isDevelopmentMode = config.ENVIRONMENT === 'dev'
  const environmentBadgeLabel = getEnvironmentBadgeLabel(config.ENVIRONMENT)
  const translate = useTranslation()
  const isSkytech = useIsSkytech()
  const isThreeStepIT = useIsThreeStepIT()
  const { whiteLabel, isMobileNavBar, setIsMobileNavBar } = useAppContext()
  const user = useUser()

  const toggleNavBar = () => {
    setIsMobileNavBar(!isMobileNavBar)
  }

  return (
    <div
      id="banner"
      className={twJoin(
        'shadow-basic flex items-center justify-between px-2 py-1 sm:px-4 sm:py-2 md:py-3',
        whiteLabel,
        environmentBadgeLabel && 'lg:justify-between'
      )}
    >
      {user && (
        <div
          className={twJoin(
            'hidden flex-row items-start justify-start gap-1 lg:flex',
            isThreeStepIT ? 'fill-threeStepIT-seaMistPrimary' : undefined
          )}
        >
          <AccountCircleIcon
            className="h-14 w-14 sm:h-16 sm:w-16"
            color={isThreeStepIT ? 'fill-threeStepIT-seaMistPrimary' : undefined}
          />

          <div className="flex flex-col items-start text-base">
            {user?.name}
            <div className="text-xs opacity-80">{phoneNumber(user.code)}</div>
            <div className="text-xs opacity-80">
              {user.departmentCode} - {user.departmentName}
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center md:gap-2">
        <button
          id="toggler"
          className={twMerge(
            whiteLabel,
            'm-0 flex cursor-pointer appearance-none border-none bg-transparent p-0 text-white lg:hidden'
          )}
          onClick={toggleNavBar}
        >
          {!user || isMobileNavBar ? (
            <CloseIcon className="size-6 sm:size-8" />
          ) : (
            <TogglerIcon className="size-6 sm:size-8" />
          )}
        </button>

        <NotificationBell />

        {isDevelopmentMode && isSkytech && <DevelopmentModeBanner />}
      </div>

      <div className="flex w-fit items-center">
        <Link to="/">
          {whiteLabel === 'ice' ? (
            <div className="flex items-center">
              <Image className="h-10 sm:h-14" alt={translate('Logo')} src={IceLogo} />
            </div>
          ) : whiteLabel === 'bechtle' ? (
            <div className="flex items-center">
              <Image className="h-10 sm:h-14" alt={translate('Logo')} src={BechtleLogo} />
            </div>
          ) : whiteLabel === 'elkjop' ? (
            <div className="flex items-center">
              <Image className="h-10 sm:h-14" alt={translate('Logo')} src={ElkjopLogo} />
            </div>
          ) : whiteLabel === 'telia' ? (
            <div className="flex items-center">
              <Image className="h-10 sm:h-14" alt={translate('Logo')} src={TeliaLogo} />
            </div>
          ) : whiteLabel === 'threeStepIT' ? (
            <div className="flex items-center max-sm:my-1">
              <Image className="h-8 sm:h-12" alt={translate('Logo')} src={ThreeStepITLogo} />
            </div>
          ) : whiteLabel === 'komplett' ? (
            <div className="flex items-center max-sm:my-1">
              <Image className="h-8 sm:h-12 md:h-14" alt={translate('Logo')} src={KomplettLogo} />
            </div>
          ) : whiteLabel === 'tietoevry' ? (
            <div className="flex items-center max-sm:my-1">
              <Image className="h-7 sm:h-10" alt={translate('Logo')} src={TietoEvryLogo} />
            </div>
          ) : whiteLabel === 'mobit' ? (
            <div className="flex items-center">
              <Image className="h-10 sm:h-14" alt={translate('Logo')} src={MobitLogo} />
            </div>
          ) : whiteLabel === 'csi' ? (
            <div className="flex items-center">
              <Image className="h-10 sm:h-14" alt={translate('Logo')} src={CsiLogo} />
            </div>
          ) : whiteLabel === 'ikomm' ? (
            <div className="flex items-center">
              <Image className="h-10 sm:h-14" alt={translate('Logo')} src={IkommLogo} />
            </div>
          ) : (
            <div className="flex items-center">
              <Image className="w-28" alt={translate('Logo')} src={SkytechLogo} />
            </div>
          )}
        </Link>
      </div>
    </div>
  )
}

const DevelopmentModeBanner: FC = () => {
  const { whiteLabel, setWhiteLabel, locale, setLocale } = useAppContext()

  return (
    <div id="dev-selectors" className={twJoin('my-auto flex flex-row items-center justify-start')}>
      <div className="border-1 border-red/50 hidden gap-2 rounded-lg border-solid p-1 md:flex">
        <Select
          id="select-white-label"
          className="w-32"
          value={whiteLabel}
          options={WHITE_LABEL_LIST.map(wl => ({ value: wl, label: wl }))}
          onChange={v => {
            if (v !== null) {
              setWhiteLabel(v?.value)
            }
          }}
        />
        <Select
          id="select-locale"
          className="w-20"
          value={locale}
          options={availableLanguages.map(lang => ({ value: lang, label: lang }))}
          onChange={v => {
            if (v !== null) {
              setLocale(v?.value)
            }
          }}
        />
      </div>
    </div>
  )
}
